"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { useVoiceChat } from "@/hooks/useVoiceChat";
import { useSocket } from "@/hooks/useSocket";
import { UserSettingsModal } from "@/components/modals/UserSettingsModal";
import { ServerSettingsModal } from "@/components/modals/ServerSettingsModal";
import {
  Hash,
  Volume2,
  Plus,
  Search,
  Settings,
  Send,
  Smile,
  ChevronDown,
  LogOut,
  Users,
  Phone,
  Video,
  Pin,
  Mic,
  MicOff,
  Headphones,
  PhoneOff,
  MessageCircle,
  UserPlus,
  Check,
  X,
  Reply,
  Paperclip,
  Image,
  Gift,
  AtSign,
  MoreHorizontal,
  Edit2,
  Trash2,
  Copy,
  Bell,
  BellOff,
  Crown,
  Shield,
  Star,
  Compass,
} from "lucide-react";

interface User {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  status: string;
  custom_status: string | null;
  bio?: string | null;
}

interface Server {
  id: string;
  name: string;
  icon_url: string | null;
  member_count: number;
  invite_code?: string;
  owner_id?: string;
  description?: string;
}

interface Channel {
  id: string;
  name: string;
  type: string;
  topic: string | null;
}

interface Message {
  id: string;
  content: string;
  author_id: string;
  created_at: string;
  author?: User;
  edited?: boolean;
  pinned?: boolean;
  reply_to_id?: string;
  attachments?: { url: string; filename: string; type: string }[];
}

interface Member {
  id: string;
  user_id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  status: string;
  custom_status: string | null;
  role: string;
}

export default function AppPage() {
  const router = useRouter();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // State
  const [user, setUser] = useState<User | null>(null);
  const [servers, setServers] = useState<Server[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  
  const [activeServerId, setActiveServerId] = useState<string | null>(null);
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  
  const [messageInput, setMessageInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [showCreateServer, setShowCreateServer] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showProfile, setShowProfile] = useState<User | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showDM, setShowDM] = useState<User | null>(null);
  const [dmMessages, setDmMessages] = useState<any[]>([]);
  const [dmInput, setDmInput] = useState("");
  const [contextMenu, setContextMenu] = useState<{x: number; y: number; type: string; data: any} | null>(null);
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<any | null>(null);
  const [showPinnedMessages, setShowPinnedMessages] = useState(false);
  const [pinnedMessages, setPinnedMessages] = useState<any[]>([]);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [showMemberList, setShowMemberList] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showServerSettings, setShowServerSettings] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any>(null);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // DM & Friends
  const [activeView, setActiveView] = useState<"servers" | "dms">("servers");
  const [friends, setFriends] = useState<User[]>([]);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [dmConversations, setDmConversations] = useState<User[]>([]);
  const [activeDMUser, setActiveDMUser] = useState<User | null>(null);
  const [unreadDMs, setUnreadDMs] = useState<Record<string, number>>({});
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);
  const [newServerName, setNewServerName] = useState("");
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelType, setNewChannelType] = useState<"text" | "voice">("text");
  const [editingChannel, setEditingChannel] = useState<any | null>(null);
  const [editChannelName, setEditChannelName] = useState("");
  const [editChannelTopic, setEditChannelTopic] = useState("");
  const [showDiscover, setShowDiscover] = useState(false);
  const [discoverServers, setDiscoverServers] = useState<any[]>([]);
  const [unreadChannels, setUnreadChannels] = useState<Record<string, number>>({});
  const [mentionedChannels, setMentionedChannels] = useState<Record<string, boolean>>({});
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");

  // Voice chat
  const voice = useVoiceChat(user?.id || "");
  
  // Real-time socket - basic functionality only
  const { 
    joinChannel, 
    sendMessage, 
    sendDM, 
    onMessage, 
    onDM,
  } = useSocket(user?.id);

  // Request notification permission and setup audio
  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      Notification.requestPermission();
    }
    // Create audio element for notification sound
    const audio = new Audio("/sounds/notification.mp3");
    audio.volume = 0.5;
    notificationAudioRef.current = audio;
  }, []);

  // Fetch current user
  useEffect(() => {
    async function fetchUser() {
      try {
        const res = await fetch("/api/auth/me");
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
        } else {
          // Not logged in, redirect to login
          router.push("/login");
        }
      } catch (err) {
        console.error("Failed to fetch user:", err);
        setError("Failed to load user");
      }
    }
    fetchUser();
  }, [router]);

  // Fetch servers (only once when user loads)
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    
    async function fetchServers() {
      try {
        const res = await fetch("/api/servers");
        if (res.ok && mounted) {
          const data = await res.json();
          setServers(data.servers || []);
          if (data.servers?.length > 0) {
            setActiveServerId(data.servers[0].id);
          }
        }
      } catch (err) {
        console.error("Failed to fetch servers:", err);
      }
    }
    fetchServers();
    
    // Also fetch friends
    async function fetchFriends() {
      try {
        const res = await fetch("/api/friends");
        if (res.ok) {
          const data = await res.json();
          setFriends(data.friends || []);
          setPendingRequests(data.pending || []);
        }
      } catch (err) {
        console.error("Failed to fetch friends:", err);
      }
    }
    fetchFriends();
    
    return () => { mounted = false; };
  }, [user?.id]);

  // Fetch channels when server changes
  useEffect(() => {
    if (!activeServerId) return;
    let mounted = true;
    
    async function fetchChannels() {
      try {
        const res = await fetch(`/api/servers/${activeServerId}/channels`);
        if (res.ok && mounted) {
          const data = await res.json();
          setChannels(data.channels || []);
          const textChannels = (data.channels || []).filter((c: Channel) => c.type === "text");
          if (textChannels.length > 0) {
            setActiveChannelId(textChannels[0].id);
          }
        }
      } catch (err) {
        console.error("Failed to fetch channels:", err);
      }
    }
    fetchChannels();
    
    return () => { mounted = false; };
  }, [activeServerId]);

  // Fetch members when server changes
  useEffect(() => {
    async function fetchMembers() {
      if (!activeServerId) return;
      try {
        const res = await fetch(`/api/servers/${activeServerId}/members`);
        if (res.ok) {
          const data = await res.json();
          setMembers(data.members || []);
        }
      } catch (err) {
        console.error("Failed to fetch members:", err);
      }
    }
    fetchMembers();
  }, [activeServerId]);

  // Fetch messages when channel changes + join socket room
  useEffect(() => {
    async function fetchMessages() {
      if (!activeChannelId) return;
      setIsLoading(true);
      
      // Join socket room for real-time updates
      joinChannel(activeChannelId);
      
      try {
        const res = await fetch(`/api/channels/${activeChannelId}/messages`);
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
        }
      } catch (err) {
        console.error("Failed to fetch messages:", err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchMessages();
  }, [activeChannelId, joinChannel]);

  // Listen for real-time messages
  useEffect(() => {
    const cleanup = onMessage((message: Message) => {
      setMessages(prev => [...prev, message]);
    });
    return cleanup;
  }, [onMessage]);

  // Listen for real-time DMs
  useEffect(() => {
    const cleanup = onDM((message: any) => {
      const senderId = message.sender_id;
      
      // If DM is open with this user, add to messages
      if (activeDMUser && senderId === activeDMUser.id) {
        setDmMessages(prev => [...prev, message]);
      } else {
        // Otherwise, increment unread count
        setUnreadDMs(prev => ({
          ...prev,
          [senderId]: (prev[senderId] || 0) + 1
        }));
        
        // Play notification sound
        notificationAudioRef.current?.play().catch(() => {});
        
        // Show browser notification
        if (Notification.permission === "granted") {
          new Notification("New Message", {
            body: `${message.sender_name || "Someone"}: ${message.content}`,
            icon: "/favicon.ico"
          });
        }
      }
    });
    return cleanup;
  }, [onDM, activeDMUser]);

  // Poll for new DMs (every 10 seconds) - reduced frequency, real-time handles most cases
  useEffect(() => {
    if (!user?.id) return;
    
    const interval = setInterval(async () => {
      try {
        const res = await fetch("/api/dm/unread");
        if (res.ok) {
          const data = await res.json();
          if (data.unread && Object.keys(data.unread).length > 0) {
            Object.entries(data.unread).forEach(([senderId, count]: [string, any]) => {
              const prevCount = unreadDMs[senderId] || 0;
              if (count > prevCount && activeDMUser?.id !== senderId) {
                notificationAudioRef.current?.play().catch(() => {});
                if (Notification.permission === "granted") {
                  new Notification("New Message", {
                    body: `You have ${count} unread message(s)`,
                    icon: "/favicon.ico"
                  });
                }
              }
            });
            setUnreadDMs(data.unread);
          }
        }
      } catch {
        // Silently ignore polling errors - real-time handles most updates
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [user?.id, activeDMUser?.id, unreadDMs]);

  // Fallback polling for channel messages (every 15 seconds) - real-time handles most updates
  useEffect(() => {
    if (!activeChannelId || activeView !== "servers") return;
    
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/channels/${activeChannelId}/messages`);
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
        }
      } catch {
        // Silently ignore - real-time handles most updates
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [activeChannelId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message
  async function handleSendMessage() {
    if ((!messageInput.trim() && !attachmentFile) || !activeChannelId || isSending) return;
    
    setIsSending(true);
    try {
      let attachmentUrl = null;
      
      // Upload attachment first if exists
      if (attachmentFile) {
        const formData = new FormData();
        formData.append("file", attachmentFile);
        formData.append("type", "attachment");
        const uploadRes = await fetch("/api/upload", { method: "POST", body: formData });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          attachmentUrl = uploadData.url;
        }
      }

      const messageBody: any = { 
        content: messageInput || (attachmentUrl ? `[Attachment](${attachmentUrl})` : ""),
      };
      
      // Add reply reference if replying
      if (replyingTo) {
        messageBody.replyToId = replyingTo.id;
      }

      const res = await fetch(`/api/channels/${activeChannelId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(messageBody),
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, data.message]);
        sendMessage(activeChannelId, data.message);
        setMessageInput("");
        setReplyingTo(null);
        setAttachmentFile(null);
      } else {
        const err = await res.json();
        setError(err.error || "Failed to send message");
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      setError("Failed to send message");
    } finally {
      setIsSending(false);
    }
  }

  // Create server
  async function handleCreateServer() {
    if (!newServerName.trim()) return;
    try {
      const res = await fetch("/api/servers/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newServerName }),
      });
      if (res.ok) {
        const data = await res.json();
        setServers(prev => [...prev, data.server]);
        setActiveServerId(data.server.id);
        setShowCreateServer(false);
        setNewServerName("");
      }
    } catch (err) {
      console.error("Failed to create server:", err);
    }
  }

  // Create channel
  async function handleCreateChannel() {
    if (!newChannelName.trim() || !activeServerId) return;
    try {
      const res = await fetch(`/api/servers/${activeServerId}/channels/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newChannelName, type: newChannelType }),
      });
      if (res.ok) {
        const data = await res.json();
        setChannels(prev => [...prev, data.channel]);
        setShowCreateChannel(false);
        setNewChannelName("");
        setNewChannelType("text");
      }
    } catch (err) {
      console.error("Failed to create channel:", err);
    }
  }

  // Edit channel
  async function handleEditChannel() {
    if (!editingChannel || !editChannelName.trim()) return;
    try {
      const res = await fetch(`/api/channels/${editingChannel.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editChannelName, topic: editChannelTopic }),
      });
      if (res.ok) {
        const data = await res.json();
        setChannels(prev => prev.map(c => c.id === editingChannel.id ? data.channel : c));
        setEditingChannel(null);
      }
    } catch (err) {
      console.error("Failed to edit channel:", err);
    }
  }

  // Delete channel
  async function handleDeleteChannel(channelId: string) {
    if (!confirm("Delete this channel? All messages will be lost.")) return;
    try {
      const res = await fetch(`/api/channels/${channelId}`, { method: "DELETE" });
      if (res.ok) {
        setChannels(prev => prev.filter(c => c.id !== channelId));
        if (activeChannelId === channelId) {
          setActiveChannelId(channels[0]?.id || null);
        }
        setEditingChannel(null);
      }
    } catch (err) {
      console.error("Failed to delete channel:", err);
    }
  }

  // Kick member
  async function handleKickMember(memberId: string) {
    if (!confirm("Kick this member from the server?")) return;
    try {
      const res = await fetch(`/api/servers/${activeServerId}/members/${memberId}`, { method: "DELETE" });
      if (res.ok) {
        setMembers(prev => prev.filter(m => m.user_id !== memberId));
      }
    } catch (err) {
      console.error("Failed to kick member:", err);
    }
  }

  // Ban member
  async function handleBanMember(memberId: string) {
    if (!confirm("Ban this member from the server?")) return;
    try {
      const res = await fetch(`/api/servers/${activeServerId}/members/${memberId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "ban" }),
      });
      if (res.ok) {
        setMembers(prev => prev.filter(m => m.user_id !== memberId));
      }
    } catch (err) {
      console.error("Failed to ban member:", err);
    }
  }

  // Handle logout
  async function handleLogout() {
    await fetch("/api/auth/logout", { method: "POST" });
    document.cookie = "nexus-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    router.push("/login");
  }

  // Load discover servers
  async function loadDiscoverServers() {
    try {
      const res = await fetch("/api/servers/discover");
      if (res.ok) {
        const data = await res.json();
        setDiscoverServers(data.servers || []);
      }
    } catch (err) {
      console.error("Failed to load discover servers:", err);
    }
  }

  // Join server
  async function handleJoinServer(serverId: string) {
    try {
      const res = await fetch(`/api/servers/${serverId}/join`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        setServers(prev => [...prev, data.server]);
        setActiveServerId(data.server.id);
        setShowDiscover(false);
      } else {
        const err = await res.json();
        alert(err.error || "Failed to join server");
      }
    } catch (err) {
      console.error("Failed to join server:", err);
    }
  }

  // Leave server
  async function handleLeaveServer() {
    if (!activeServerId) return;
    if (!confirm("Are you sure you want to leave this server?")) return;
    try {
      const res = await fetch(`/api/servers/${activeServerId}/leave`, { method: "POST" });
      if (res.ok) {
        setServers(prev => prev.filter(s => s.id !== activeServerId));
        setActiveServerId(servers[0]?.id || null);
      } else {
        const err = await res.json();
        alert(err.error || "Failed to leave server");
      }
    } catch (err) {
      console.error("Failed to leave server:", err);
    }
  }

  // Add friend
  async function handleAddFriend(friendId: string) {
    try {
      const res = await fetch("/api/friends", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ friendId, action: "add" }),
      });
      if (res.ok) {
        alert("Friend request sent!");
      } else {
        const data = await res.json();
        alert(data.error || "Failed to send request");
      }
    } catch (err) {
      console.error("Add friend error:", err);
    }
  }

  // Accept friend request
  async function handleAcceptFriend(friendId: string) {
    try {
      const res = await fetch("/api/friends", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ friendId, action: "accept" }),
      });
      if (res.ok) {
        setPendingRequests(prev => prev.filter(r => r.friend_id !== friendId));
        // Refresh friends list
        const friendsRes = await fetch("/api/friends");
        if (friendsRes.ok) {
          const data = await friendsRes.json();
          setFriends(data.friends || []);
        }
      }
    } catch (err) {
      console.error("Accept friend error:", err);
    }
  }

  // Reject friend request
  async function handleRejectFriend(friendId: string) {
    try {
      const res = await fetch("/api/friends", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ friendId, action: "reject" }),
      });
      if (res.ok) {
        setPendingRequests(prev => prev.filter(r => r.friend_id !== friendId));
      }
    } catch (err) {
      console.error("Reject friend error:", err);
    }
  }

  // Start DM conversation
  async function startDMConversation(targetUser: User) {
    setActiveView("dms");
    setActiveDMUser(targetUser);
    setShowDM(null);
    // Clear unread count for this user
    setUnreadDMs(prev => {
      const updated = { ...prev };
      delete updated[targetUser.id];
      return updated;
    });
    try {
      const res = await fetch(`/api/dm/${targetUser.id}`);
      if (res.ok) {
        const data = await res.json();
        setDmMessages(data.messages || []);
      }
    } catch (err) {
      console.error("Failed to load DMs:", err);
    }
  }

  // Block user
  async function handleBlockUser(userId: string) {
    if (!confirm("Are you sure you want to block this user?")) return;
    try {
      const res = await fetch(`/api/users/${userId}/block`, { method: "POST" });
      if (res.ok) {
        alert("User blocked");
      }
    } catch (err) {
      console.error("Block error:", err);
    }
  }

  // Edit message
  async function handleEditMessage(messageId: string) {
    if (!editContent.trim()) return;
    try {
      const res = await fetch(`/api/channels/${activeChannelId}/messages/${messageId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: editContent }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => prev.map(m => m.id === messageId ? data.message : m));
        setEditingMessage(null);
        setEditContent("");
      }
    } catch (err) {
      console.error("Edit message error:", err);
    }
  }

  // Delete message
  async function handleDeleteMessage(messageId: string) {
    if (!confirm("Delete this message?")) return;
    try {
      const res = await fetch(`/api/channels/${activeChannelId}/messages/${messageId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setMessages(prev => prev.filter(m => m.id !== messageId));
      }
    } catch (err) {
      console.error("Delete message error:", err);
    }
  }

  // Add reaction
  async function handleAddReaction(messageId: string, emoji: string) {
    try {
      await fetch(`/api/channels/${activeChannelId}/messages/${messageId}/reactions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emoji }),
      });
      setShowEmojiPicker(null);
    } catch (err) {
      console.error("Add reaction error:", err);
    }
  }

  // Pin message
  async function handlePinMessage(messageId: string) {
    try {
      const res = await fetch(`/api/channels/${activeChannelId}/pins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messageId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.action === "pinned") {
          setMessages(prev => prev.map(m => m.id === messageId ? { ...m, pinned: true } : m));
        } else {
          setMessages(prev => prev.map(m => m.id === messageId ? { ...m, pinned: false } : m));
        }
      }
    } catch (err) {
      console.error("Pin message error:", err);
    }
  }

  // Load pinned messages
  async function loadPinnedMessages() {
    if (!activeChannelId) return;
    try {
      const res = await fetch(`/api/channels/${activeChannelId}/pins`);
      if (res.ok) {
        const data = await res.json();
        setPinnedMessages(data.pins || []);
      }
    } catch (err) {
      console.error("Load pins error:", err);
    }
  }

  // Upload attachment
  async function handleFileUpload(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "attachment");
    
    try {
      const res = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        return data.url;
      }
    } catch (err) {
      console.error("Upload error:", err);
    }
    return null;
  }

  // Copy message
  function handleCopyMessage(content: string) {
    navigator.clipboard.writeText(content);
  }

  // Search
  async function handleSearch() {
    if (!searchQuery.trim()) return;
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(searchQuery)}&serverId=${activeServerId || ""}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
      }
    } catch (err) {
      console.error("Search error:", err);
    }
  }

  // Typing indicator
  function handleTyping() {
    if (!isTyping) {
      setIsTyping(true);
      // Emit typing event via socket
      // socket?.emit("typing", { channelId: activeChannelId, userId: user?.id });
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 3000);
  }

  // Open DM
  async function handleOpenDM(targetUser: User) {
    setShowDM(targetUser);
    try {
      const res = await fetch(`/api/dm/${targetUser.id}`);
      if (res.ok) {
        const data = await res.json();
        setDmMessages(data.messages || []);
      }
    } catch (err) {
      console.error("Failed to load DMs:", err);
    }
  }

  // Send DM
  async function handleSendDM() {
    const targetUser = activeDMUser || showDM;
    if (!dmInput.trim() || !targetUser) return;
    try {
      const res = await fetch(`/api/dm/${targetUser.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: dmInput }),
      });
      if (res.ok) {
        const data = await res.json();
        setDmMessages(prev => [...prev, data.message]);
        // Broadcast to recipient via socket
        console.log("Sending DM via socket to:", targetUser.id, data.message);
        sendDM(targetUser.id, data.message);
        setDmInput("");
      }
    } catch (err) {
      console.error("Send DM error:", err);
    }
  }

  // Handle key press
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }

  const activeServer = servers.find(s => s.id === activeServerId);
  const activeChannel = channels.find(c => c.id === activeChannelId);
  const textChannels = channels.filter(c => c.type === "text");
  const voiceChannels = channels.filter(c => c.type === "voice");

  if (!user) {
    return (
      <div className="h-screen w-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-zinc-900 text-zinc-100 flex overflow-hidden">
      {/* Server Sidebar */}
      <nav className="w-[72px] bg-zinc-950 flex flex-col items-center py-3 gap-2">
        {/* DM Button */}
        <button
          onClick={() => { setActiveView("dms"); setActiveServerId(null); }}
          className={cn(
            "w-12 h-12 rounded-2xl flex items-center justify-center transition-all mb-2 relative",
            activeView === "dms"
              ? "bg-violet-600 rounded-xl"
              : "bg-zinc-800 hover:bg-zinc-700 hover:rounded-xl"
          )}
        >
          <MessageCircle className="w-5 h-5" />
          {Object.values(unreadDMs).reduce((a, b) => a + b, 0) > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs font-bold flex items-center justify-center">
              {Object.values(unreadDMs).reduce((a, b) => a + b, 0)}
            </span>
          )}
        </button>
        <div className="w-8 h-0.5 bg-zinc-700 rounded mb-2" />
        
        {/* Servers */}
        <div className="flex flex-col items-center gap-2 flex-1 overflow-y-auto">
          {servers.map((server) => (
            <button
              key={server.id}
              onClick={() => {
                setActiveView("servers");
                setActiveServerId(server.id);
                setActiveChannelId(null);
                setActiveDMUser(null);
              }}
              className={cn(
                "w-12 h-12 rounded-2xl flex items-center justify-center transition-all flex-shrink-0",
                activeServerId === server.id && activeView === "servers"
                  ? "bg-violet-600 rounded-xl"
                  : "bg-zinc-800 hover:bg-zinc-700 hover:rounded-xl"
              )}
            >
              <span className="text-lg font-bold text-white">
                {server.name.charAt(0).toUpperCase()}
              </span>
            </button>
          ))}
          
          {/* Add Server */}
          <button 
            onClick={() => setShowCreateServer(true)}
            className="w-12 h-12 rounded-2xl border-2 border-dashed border-zinc-700 flex items-center justify-center hover:border-violet-500 hover:text-violet-500 transition-colors flex-shrink-0"
            title="Create Server"
          >
            <Plus className="w-5 h-5" />
          </button>
          
          {/* Discover Servers */}
          <button 
            onClick={() => { setShowDiscover(true); loadDiscoverServers(); }}
            className="w-12 h-12 rounded-2xl bg-zinc-800 flex items-center justify-center hover:bg-green-600 hover:rounded-xl transition-all flex-shrink-0"
            title="Explore Servers"
          >
            <Compass className="w-5 h-5" />
          </button>
        </div>

        {/* User Actions */}
        <div className="flex flex-col items-center gap-2">
          <button 
            onClick={() => setShowSettings(true)}
            className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-zinc-700"
          >
            <Settings className="w-5 h-5 text-zinc-400" />
          </button>
          <button
            onClick={handleLogout}
            className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-red-600"
          >
            <LogOut className="w-5 h-5 text-zinc-400" />
          </button>
          <button 
            onClick={() => user && setShowProfile(user)}
            className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center hover:opacity-80"
          >
            <span className="text-sm font-bold text-white">
              {user.display_name?.charAt(0) || "U"}
            </span>
          </button>
        </div>
      </nav>

      {/* Channel Sidebar / DM Sidebar */}
      <aside className="w-60 bg-zinc-800 flex flex-col">
        {activeView === "dms" ? (
          <>
            {/* DM Header */}
            <div className="h-12 px-4 flex items-center border-b border-zinc-700 shadow">
              <h2 className="font-semibold">Direct Messages</h2>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {/* Pending Friend Requests */}
              {pendingRequests.length > 0 && (
                <div className="mb-4">
                  <div className="px-2 py-1 text-xs font-semibold text-zinc-400 uppercase">
                    Friend Requests — {pendingRequests.length}
                  </div>
                  {pendingRequests.map((req) => (
                    <div key={req.id} className="flex items-center gap-2 px-2 py-2 rounded bg-zinc-700/50 mb-1">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">{req.display_name?.charAt(0) || "U"}</span>
                      </div>
                      <span className="text-sm truncate flex-1">{req.display_name}</span>
                      <button onClick={() => handleAcceptFriend(req.friend_id)} className="p-1 hover:bg-green-600 rounded">
                        <Check className="w-4 h-4 text-green-400" />
                      </button>
                      <button onClick={() => handleRejectFriend(req.friend_id)} className="p-1 hover:bg-red-600 rounded">
                        <X className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Friends List */}
              <div className="mb-4">
                <div className="px-2 py-1 text-xs font-semibold text-zinc-400 uppercase">
                  Friends — {friends.length}
                </div>
                {friends.length === 0 ? (
                  <p className="text-xs text-zinc-500 px-2 py-2">No friends yet. Add some!</p>
                ) : (
                  friends.map((friend: any) => (
                    <button
                      key={friend.friend_id}
                      onClick={() => startDMConversation({
                        id: friend.friend_id,
                        username: friend.username,
                        display_name: friend.display_name,
                        avatar_url: friend.avatar_url,
                        status: friend.status,
                        custom_status: null,
                      })}
                      className={cn(
                        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-left",
                        activeDMUser?.id === friend.friend_id
                          ? "bg-zinc-700 text-white"
                          : "text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200"
                      )}
                    >
                      <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
                          <span className="text-xs font-bold text-white">{friend.display_name?.charAt(0) || "U"}</span>
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-zinc-800 ${friend.status === 'online' ? 'bg-green-500' : 'bg-zinc-500'}`} />
                      </div>
                      <span className="truncate flex-1">{friend.display_name}</span>
                      {unreadDMs[friend.friend_id] > 0 && (
                        <span className="w-5 h-5 bg-red-500 rounded-full text-xs font-bold flex items-center justify-center">
                          {unreadDMs[friend.friend_id]}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Server Header with Dropdown */}
            <div className="relative group">
              <button 
                className="h-12 px-4 flex items-center justify-between border-b border-zinc-700 shadow hover:bg-zinc-700/50 w-full text-left"
              >
                <h2 className="font-semibold truncate">{activeServer?.name || "Select Server"}</h2>
                <ChevronDown className="w-4 h-4 text-zinc-400" />
              </button>
              {/* Dropdown Menu */}
              <div className="absolute top-full left-2 right-2 mt-1 bg-zinc-900 rounded-lg shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <button 
                  onClick={() => setShowInvite(true)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-violet-600 flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite People
                </button>
                <button 
                  onClick={() => setShowServerSettings(true)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-700 flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Server Settings
                </button>
                <button 
                  onClick={() => { setNewChannelType("text"); setShowCreateChannel(true); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-700 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Channel
                </button>
                <div className="border-t border-zinc-700 my-1" />
                <button 
                  onClick={handleLeaveServer}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-700 flex items-center gap-2 text-red-400"
                >
                  <LogOut className="w-4 h-4" />
                  Leave Server
                </button>
              </div>
            </div>

            {/* Channels */}
            <div className="flex-1 overflow-y-auto p-2">
              {/* Text Channels */}
              <div className="mb-4">
                <div className="flex items-center justify-between px-2 py-1 text-xs font-semibold text-zinc-400 uppercase">
                  <span>Text Channels</span>
                  <Plus 
                    onClick={() => { setNewChannelType("text"); setShowCreateChannel(true); }}
                    className="w-4 h-4 cursor-pointer hover:text-white" 
                  />
                </div>
                {textChannels.map((channel) => {
                  const unreadCount = unreadChannels[channel.id] || 0;
                  const hasMention = mentionedChannels[channel.id] || false;
                  return (
                    <div
                      key={channel.id}
                      className="group relative"
                    >
                      <button
                        onClick={() => {
                          setActiveChannelId(channel.id);
                          setUnreadChannels(prev => ({ ...prev, [channel.id]: 0 }));
                          setMentionedChannels(prev => ({ ...prev, [channel.id]: false }));
                        }}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-1.5 rounded text-left",
                          activeChannelId === channel.id
                            ? "bg-zinc-700 text-white"
                            : unreadCount > 0
                              ? "text-white font-semibold hover:bg-zinc-700/50"
                              : "text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200"
                        )}
                      >
                        <Hash className={cn("w-4 h-4", hasMention && "text-red-400")} />
                        <span className="truncate flex-1">{channel.name}</span>
                        {unreadCount > 0 && (
                          <span className={cn(
                            "min-w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold",
                            hasMention ? "bg-red-500 text-white" : "bg-zinc-500 text-white"
                          )}>
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                        <Settings 
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingChannel(channel);
                          }}
                          className="w-4 h-4 opacity-0 group-hover:opacity-100 hover:text-white"
                        />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Voice Channels */}
              <div>
                <div className="flex items-center justify-between px-2 py-1 text-xs font-semibold text-zinc-400 uppercase">
                  <span>Voice Channels</span>
                  <Plus 
                    onClick={() => { setNewChannelType("voice"); setShowCreateChannel(true); }}
                    className="w-4 h-4 cursor-pointer hover:text-white" 
                  />
                </div>
                {voiceChannels.map((channel) => (
                  <div key={channel.id}>
                    <button
                      onClick={() => voice.isInVoice && voice.currentChannel === channel.id 
                        ? voice.leaveVoice() 
                        : voice.joinVoice(channel.id)
                      }
                      className={cn(
                        "w-full flex items-center gap-2 px-2 py-1.5 rounded text-left",
                        voice.currentChannel === channel.id
                          ? "bg-green-600/20 text-green-400"
                          : "text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200"
                      )}
                    >
                      <Volume2 className="w-4 h-4" />
                      <span className="truncate flex-1">{channel.name}</span>
                      {voice.currentChannel === channel.id && (
                        <span className="text-xs bg-green-600 px-1.5 py-0.5 rounded">LIVE</span>
                      )}
                    </button>
                    {voice.currentChannel === channel.id && voice.participants.length > 0 && (
                      <div className="ml-6 mt-1 space-y-1">
                        {voice.participants.map((p) => {
                          const member = members.find(m => m.user_id === p.odId);
                          return (
                            <div key={p.socketId} className="flex items-center gap-2 text-xs text-zinc-400">
                              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
                                <span className="text-[10px] text-white font-bold">
                                  {member?.display_name?.charAt(0) || "U"}
                                </span>
                              </div>
                              <span>{member?.display_name || "User"}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Voice Controls (when in voice) */}
        {voice.isInVoice && (
          <div className="p-2 bg-zinc-900/80 border-t border-zinc-700">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-green-400">Voice Connected</span>
              </div>
              <button
                onClick={voice.leaveVoice}
                className="p-1.5 rounded bg-red-600 hover:bg-red-500 transition-colors"
              >
                <PhoneOff className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={voice.toggleMute}
                className={cn(
                  "flex-1 p-2 rounded flex items-center justify-center gap-2 transition-colors",
                  voice.isMuted ? "bg-red-600/20 text-red-400" : "bg-zinc-700 text-zinc-200 hover:bg-zinc-600"
                )}
              >
                {voice.isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                <span className="text-xs">{voice.isMuted ? "Unmute" : "Mute"}</span>
              </button>
              <button
                onClick={voice.toggleDeafen}
                className={cn(
                  "flex-1 p-2 rounded flex items-center justify-center gap-2 transition-colors",
                  voice.isDeafened ? "bg-red-600/20 text-red-400" : "bg-zinc-700 text-zinc-200 hover:bg-zinc-600"
                )}
              >
                <Headphones className="w-4 h-4" />
                <span className="text-xs">{voice.isDeafened ? "Undeafen" : "Deafen"}</span>
              </button>
            </div>
          </div>
        )}

        {/* User Panel */}
        <div className="h-14 px-2 flex items-center gap-2 bg-zinc-900">
          <div className="relative">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {user.display_name?.charAt(0) || "U"}
                </span>
              </div>
            )}
            <div className={cn(
              "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-zinc-900",
              user.status === "online" ? "bg-green-500" :
              user.status === "idle" ? "bg-yellow-500" :
              user.status === "dnd" ? "bg-red-500" : "bg-gray-500"
            )} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user.display_name}</p>
            <p className="text-xs text-zinc-400 truncate">
              {voice.isInVoice ? "In Voice" : user.custom_status || (user.status === "online" ? "Online" : user.status === "idle" ? "Idle" : user.status === "dnd" ? "Do Not Disturb" : "Offline")}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={voice.toggleMute}
              className={cn("p-1.5 rounded hover:bg-zinc-700", voice.isMuted && "text-red-400")}
              title={voice.isMuted ? "Unmute" : "Mute"}
            >
              {voice.isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <button
              onClick={voice.toggleDeafen}
              className={cn("p-1.5 rounded hover:bg-zinc-700", voice.isDeafened && "text-red-400")}
              title={voice.isDeafened ? "Undeafen" : "Deafen"}
            >
              <Headphones className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-1.5 rounded hover:bg-zinc-700"
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Chat Area */}
      <main className="flex-1 flex flex-col bg-zinc-700">
        {activeView === "dms" && activeDMUser ? (
          <>
            {/* DM Header */}
            <div className="h-12 px-4 flex items-center justify-between border-b border-zinc-600 shadow">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
                  <span className="text-xs font-bold text-white">{activeDMUser.display_name?.charAt(0) || "U"}</span>
                </div>
                <span className="font-semibold">{activeDMUser.display_name}</span>
                <span className="text-sm text-zinc-400">@{activeDMUser.username}</span>
              </div>
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-zinc-600 rounded">
                  <Phone className="w-5 h-5 text-zinc-400" />
                </button>
                <button className="p-2 hover:bg-zinc-600 rounded">
                  <Video className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
            </div>

            {/* DM Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {dmMessages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-white">{activeDMUser.display_name?.charAt(0) || "U"}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-2">{activeDMUser.display_name}</h3>
                    <p className="text-zinc-400">This is the beginning of your conversation with {activeDMUser.display_name}.</p>
                  </div>
                </div>
              ) : (
                dmMessages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] px-4 py-2 rounded-2xl ${msg.sender_id === user?.id ? 'bg-violet-600' : 'bg-zinc-600'}`}>
                      <p className="text-sm">{msg.content}</p>
                      <p className="text-[10px] text-zinc-300 mt-1">{new Date(msg.created_at).toLocaleTimeString()}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* DM Input */}
            <div className="p-4 border-t border-zinc-600">
              <div className="flex items-center gap-2 bg-zinc-600 rounded-lg px-4 py-2">
                <button className="text-zinc-400 hover:text-zinc-200">
                  <Plus className="w-5 h-5" />
                </button>
                <input
                  type="text"
                  value={dmInput}
                  onChange={(e) => setDmInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendDM();
                    }
                  }}
                  placeholder={`Message @${activeDMUser.username}`}
                  className="flex-1 bg-transparent text-zinc-100 placeholder:text-zinc-400 outline-none"
                />
                <button className="text-zinc-400 hover:text-zinc-200">
                  <Smile className="w-5 h-5" />
                </button>
                <button onClick={handleSendDM} className="text-zinc-400 hover:text-violet-400">
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        ) : activeView === "dms" ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-zinc-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Your Direct Messages</h3>
              <p className="text-zinc-400">Select a friend to start chatting</p>
            </div>
          </div>
        ) : (
          <>
            {/* Channel Header */}
            <div className="h-12 px-4 flex items-center justify-between border-b border-zinc-600 shadow">
              <div className="flex items-center gap-2">
                <Hash className="w-5 h-5 text-zinc-400" />
                <span className="font-semibold">{activeChannel?.name || "general"}</span>
                {activeChannel?.topic && (
                  <>
                    <div className="w-px h-4 bg-zinc-600 mx-2" />
                    <span className="text-sm text-zinc-400 truncate">{activeChannel.topic}</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-zinc-600 rounded" title="Start Voice Call">
                  <Phone className="w-5 h-5 text-zinc-400" />
                </button>
                <button className="p-2 hover:bg-zinc-600 rounded" title="Start Video Call">
                  <Video className="w-5 h-5 text-zinc-400" />
                </button>
                <button 
                  onClick={() => { loadPinnedMessages(); setShowPinnedMessages(true); }}
                  className="p-2 hover:bg-zinc-600 rounded" 
                  title="Pinned Messages"
                >
                  <Pin className="w-5 h-5 text-zinc-400" />
                </button>
                <button 
                  onClick={() => setShowMemberList(!showMemberList)}
                  className={cn("p-2 hover:bg-zinc-600 rounded", showMemberList && "bg-zinc-600")}
                  title="Toggle Member List"
                >
                  <Users className="w-5 h-5 text-zinc-400" />
                </button>
                <button 
                  onClick={() => setShowSearch(true)}
                  className="p-2 hover:bg-zinc-600 rounded flex items-center gap-2 bg-zinc-900 px-3"
                  title="Search"
                >
                  <Search className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm text-zinc-500">Search</span>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <span className="text-zinc-400">Loading messages...</span>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Hash className="w-16 h-16 text-zinc-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">Welcome to #{activeChannel?.name || "general"}!</h3>
                    <p className="text-zinc-400">This is the start of the channel. Send a message to begin.</p>
                  </div>
                </div>
              ) : (
            messages.map((msg, i) => {
              const showAvatar = i === 0 || messages[i - 1]?.author_id !== msg.author_id;
              const isOwn = msg.author_id === user?.id;
              const isEditing = editingMessage === msg.id;
              
              return (
                <div key={msg.id} className={cn("group flex gap-3 hover:bg-zinc-600/30 -mx-4 px-4 py-1 rounded", isOwn && "flex-row-reverse")}>
                  {showAvatar ? (
                    <div 
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center flex-shrink-0 cursor-pointer"
                      onClick={() => setShowProfile(msg.author as User)}
                    >
                      {msg.author?.avatar_url ? (
                        <img src={msg.author.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <span className="text-sm font-bold text-white">
                          {msg.author?.display_name?.charAt(0) || "U"}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="w-10" />
                  )}
                  <div className={cn("max-w-[70%] relative", isOwn && "text-right")}>
                    {showAvatar && (
                      <div className={cn("flex items-center gap-2 mb-1", isOwn && "flex-row-reverse")}>
                        <span className="text-sm font-semibold text-white hover:underline cursor-pointer" onClick={() => setShowProfile(msg.author as User)}>
                          {msg.author?.display_name || "User"}
                        </span>
                        <span className="text-xs text-zinc-400">
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {msg.edited && <span className="text-xs text-zinc-500">(edited)</span>}
                      </div>
                    )}
                    {isEditing ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleEditMessage(msg.id);
                            if (e.key === "Escape") { setEditingMessage(null); setEditContent(""); }
                          }}
                          className="flex-1 px-3 py-1 rounded bg-zinc-800 text-white text-sm"
                          autoFocus
                        />
                        <button onClick={() => handleEditMessage(msg.id)} className="px-2 py-1 bg-violet-600 rounded text-xs">Save</button>
                        <button onClick={() => { setEditingMessage(null); setEditContent(""); }} className="px-2 py-1 bg-zinc-600 rounded text-xs">Cancel</button>
                      </div>
                    ) : (
                      <div className={cn(
                        "inline-block px-4 py-2 rounded-2xl text-sm",
                        isOwn
                          ? "bg-violet-600 text-white rounded-br-sm"
                          : "bg-zinc-600 text-zinc-100 rounded-bl-sm",
                        msg.content?.includes(`@${user?.username}`) && "ring-2 ring-yellow-500/50 bg-yellow-500/10"
                      )}>
                        {msg.content?.split(/(@\w+)/g).map((part, i) => 
                          part.startsWith('@') ? (
                            <span key={i} className="bg-violet-500/30 text-violet-300 px-1 rounded cursor-pointer hover:underline">
                              {part}
                            </span>
                          ) : part
                        )}
                      </div>
                    )}
                    {/* Message Actions */}
                    <div className={cn(
                      "absolute -top-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5 bg-zinc-800 rounded-lg shadow-lg border border-zinc-700",
                      isOwn ? "left-0" : "right-0"
                    )}>
                      <button 
                        onClick={() => setShowEmojiPicker(showEmojiPicker === msg.id ? null : msg.id)}
                        className="p-1.5 hover:bg-zinc-700 rounded-l-lg text-zinc-400 hover:text-white"
                        title="Add Reaction"
                      >
                        <Smile className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => setReplyingTo(msg)}
                        className="p-1.5 hover:bg-zinc-700 text-zinc-400 hover:text-white"
                        title="Reply"
                      >
                        <Reply className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handlePinMessage(msg.id)}
                        className={cn("p-1.5 hover:bg-zinc-700 text-zinc-400 hover:text-white", msg.pinned && "text-yellow-500")}
                        title={msg.pinned ? "Unpin" : "Pin"}
                      >
                        <Pin className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleCopyMessage(msg.content)}
                        className="p-1.5 hover:bg-zinc-700 text-zinc-400 hover:text-white"
                        title="Copy"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {isOwn && (
                        <>
                          <button 
                            onClick={() => { setEditingMessage(msg.id); setEditContent(msg.content); }}
                            className="p-1.5 hover:bg-zinc-700 text-zinc-400 hover:text-white"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="p-1.5 hover:bg-zinc-700 rounded-r-lg text-zinc-400 hover:text-red-400"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                    {/* Emoji Picker */}
                    {showEmojiPicker === msg.id && (
                      <div className={cn(
                        "absolute top-8 bg-zinc-800 rounded-lg shadow-xl p-2 z-10",
                        isOwn ? "right-0" : "left-0"
                      )}>
                        <div className="flex gap-1 flex-wrap w-64 max-h-48 overflow-y-auto">
                          {["👍", "👎", "❤️", "🧡", "💛", "💚", "💙", "�", "�", "🤣", "😊", "😍", "🥰", "😘", "😮", "😢", "�", "�", "🤬", "🎉", "🔥", "✨", "�", "�", "🙌", "👏", "💪", "🤝", "🙏", "✅", "❌", "⭐", "�", "📌", "🚀", "💻", "🎮", "🎵", "☕", "🍕"].map(emoji => (
                            <button
                              key={emoji}
                              onClick={() => handleAddReaction(msg.id, emoji)}
                              className="p-1.5 hover:bg-zinc-700 rounded text-lg"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

            {/* Message Input */}
            <div className="p-4">
              {/* Reply indicator */}
              {replyingTo && (
                <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-zinc-800 rounded-t-lg border-l-4 border-violet-500">
                  <Reply className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm text-zinc-400">Replying to</span>
                  <span className="text-sm font-medium text-white">{replyingTo.author?.display_name}</span>
                  <span className="text-sm text-zinc-500 truncate flex-1">{replyingTo.content}</span>
                  <button onClick={() => setReplyingTo(null)} className="p-1 hover:bg-zinc-700 rounded">
                    <X className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>
              )}
              {/* Attachment preview */}
              {attachmentFile && (
                <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-zinc-800 rounded-lg">
                  <Paperclip className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm text-zinc-300 truncate flex-1">{attachmentFile.name}</span>
                  <span className="text-xs text-zinc-500">{(attachmentFile.size / 1024).toFixed(1)} KB</span>
                  <button onClick={() => setAttachmentFile(null)} className="p-1 hover:bg-zinc-700 rounded">
                    <X className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>
              )}
              <div className={cn("flex items-center gap-2 bg-zinc-600 rounded-lg p-2", replyingTo && "rounded-t-none")}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 hover:bg-zinc-500 rounded"
                  title="Upload file"
                >
                  <Plus className="w-5 h-5 text-zinc-300" />
                </button>
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 hover:bg-zinc-500 rounded"
                  title="Upload image"
                >
                  <Image className="w-5 h-5 text-zinc-300" />
                </button>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => {
                      setMessageInput(e.target.value);
                      // Check for @ mention
                      const match = e.target.value.match(/@(\w*)$/);
                      if (match) {
                        setShowMentionList(true);
                        setMentionFilter(match[1].toLowerCase());
                      } else {
                        setShowMentionList(false);
                      }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={`Message #${activeChannel?.name || "general"}`}
                    className="w-full bg-transparent text-zinc-100 placeholder:text-zinc-400 outline-none"
                    disabled={isSending}
                  />
                  {/* Mention Autocomplete */}
                  {showMentionList && (
                    <div className="absolute bottom-full left-0 mb-2 bg-zinc-800 rounded-lg shadow-xl border border-zinc-700 max-h-48 overflow-y-auto w-64">
                      {members
                        .filter(m => m.display_name?.toLowerCase().includes(mentionFilter) || m.username?.toLowerCase().includes(mentionFilter))
                        .slice(0, 5)
                        .map(member => (
                          <button
                            key={member.id}
                            onClick={() => {
                              setMessageInput(prev => prev.replace(/@\w*$/, `@${member.username} `));
                              setShowMentionList(false);
                            }}
                            className="w-full px-3 py-2 flex items-center gap-2 hover:bg-zinc-700 text-left"
                          >
                            <div className="w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center text-xs">
                              {member.display_name?.charAt(0) || "U"}
                            </div>
                            <span className="text-sm">{member.display_name}</span>
                            <span className="text-xs text-zinc-400">@{member.username}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <button className="p-2 hover:bg-zinc-500 rounded" title="Gift Nitro">
                  <Gift className="w-5 h-5 text-zinc-300" />
                </button>
                <div className="relative">
                  <button 
                    onClick={() => setShowEmojiPicker(showEmojiPicker === 'input' ? null : 'input')}
                    className="p-2 hover:bg-zinc-500 rounded" 
                    title="Emoji"
                  >
                    <Smile className="w-5 h-5 text-zinc-300" />
                  </button>
                  {showEmojiPicker === 'input' && (
                    <div className="absolute bottom-12 right-0 bg-zinc-800 rounded-lg shadow-xl p-3 z-50">
                      <div className="flex gap-1 flex-wrap w-72 max-h-48 overflow-y-auto">
                        {["😀", "😃", "😄", "😁", "😅", "😂", "🤣", "😊", "😇", "🙂", "😉", "😍", "🥰", "😘", "😗", "😋", "😛", "😜", "🤪", "😎", "🤩", "🥳", "😏", "😒", "😞", "😔", "😟", "😕", "🙁", "😣", "😖", "😫", "😩", "🥺", "😢", "😭", "😤", "😠", "😡", "🤬", "👍", "👎", "👏", "🙌", "🤝", "🙏", "💪", "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💯", "✨", "🔥", "⭐", "🎉", "🎊", "🚀", "💻", "🎮", "🎵", "☕", "🍕", "🍔", "🌟", "💡", "📌", "✅", "❌"].map(emoji => (
                          <button
                            key={emoji}
                            onClick={() => {
                              setMessageInput(prev => prev + emoji);
                              setShowEmojiPicker(null);
                            }}
                            className="p-1.5 hover:bg-zinc-700 rounded text-lg"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={(!messageInput.trim() && !attachmentFile) || isSending}
                  className={cn(
                    "p-2 rounded transition-colors",
                    (messageInput.trim() || attachmentFile)
                      ? "bg-violet-600 hover:bg-violet-500 text-white"
                      : "bg-zinc-500 text-zinc-400 cursor-not-allowed"
                  )}
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Typing Indicator */}
            {typingUsers.length > 0 && (
              <div className="px-4 py-1 text-sm text-zinc-400 flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span>
                  {typingUsers.length === 1 
                    ? `${typingUsers[0]} is typing...`
                    : typingUsers.length === 2
                      ? `${typingUsers[0]} and ${typingUsers[1]} are typing...`
                      : `${typingUsers.length} people are typing...`
                  }
                </span>
              </div>
            )}

            {/* Error Toast */}
            {error && (
              <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg">
                {error}
                <button onClick={() => setError(null)} className="ml-2 font-bold">×</button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Members Sidebar */}
      {showMemberList && (
      <aside className="w-60 bg-zinc-800 p-3 overflow-y-auto">
        {(() => {
          // Group members by their highest hoist role
          const hoistRoles = members
            .flatMap((m: any) => m.roles?.filter((r: any) => r.hoist) || [])
            .filter((r: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.id === r.id) === i)
            .sort((a: any, b: any) => b.position - a.position);
          
          const getMemberTopRole = (member: any) => {
            const hoistRole = member.roles?.find((r: any) => r.hoist);
            return hoistRole || null;
          };

          const groupedMembers: Record<string, any[]> = { online: [], offline: [] };
          hoistRoles.forEach((role: any) => { groupedMembers[role.id] = []; });

          members.forEach((member: any) => {
            const topRole = getMemberTopRole(member);
            const isOnline = member.status !== 'offline';
            if (topRole && groupedMembers[topRole.id]) {
              groupedMembers[topRole.id].push(member);
            } else if (isOnline) {
              groupedMembers.online.push(member);
            } else {
              groupedMembers.offline.push(member);
            }
          });

          const renderMember = (member: any) => {
            const memberUser: User = {
              id: member.user_id,
              username: member.username,
              display_name: member.display_name,
              avatar_url: member.avatar_url,
              status: member.status || 'online',
              custom_status: member.custom_status,
            };
            const topRole = member.roles?.[0];
            return (
              <button
                key={member.id}
                onClick={() => setShowProfile(memberUser)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, type: 'user', data: memberUser });
                }}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-700/50 w-full text-left"
              >
                <div className="relative">
                  {member.avatar_url ? (
                    <img src={member.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
                      <span className="text-xs font-bold text-white">
                        {member.display_name?.charAt(0) || "U"}
                      </span>
                    </div>
                  )}
                  <div className={cn(
                    "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-zinc-800",
                    member.status === "online" ? "bg-green-500" :
                    member.status === "idle" ? "bg-yellow-500" :
                    member.status === "dnd" ? "bg-red-500" : "bg-gray-500"
                  )} />
                </div>
                <span 
                  className="text-sm truncate"
                  style={{ color: topRole?.color || '#e4e4e7' }}
                >
                  {member.display_name || member.username || "User"}
                </span>
              </button>
            );
          };

          return (
            <div className="space-y-4">
              {hoistRoles.map((role: any) => (
                groupedMembers[role.id]?.length > 0 && (
                  <div key={role.id}>
                    <h3 
                      className="text-xs font-semibold uppercase mb-2"
                      style={{ color: role.color || '#a1a1aa' }}
                    >
                      {role.name} — {groupedMembers[role.id].length}
                    </h3>
                    <div className="space-y-0.5">
                      {groupedMembers[role.id].map(renderMember)}
                    </div>
                  </div>
                )
              ))}
              {groupedMembers.online.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-zinc-400 uppercase mb-2">
                    Online — {groupedMembers.online.length}
                  </h3>
                  <div className="space-y-0.5">
                    {groupedMembers.online.map(renderMember)}
                  </div>
                </div>
              )}
              {groupedMembers.offline.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-zinc-500 uppercase mb-2">
                    Offline — {groupedMembers.offline.length}
                  </h3>
                  <div className="space-y-0.5 opacity-50">
                    {groupedMembers.offline.map(renderMember)}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </aside>
      )}

      {/* Create Server Modal */}
      {showCreateServer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-zinc-800 rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Create a Server</h2>
            <input
              type="text"
              value={newServerName}
              onChange={(e) => setNewServerName(e.target.value)}
              placeholder="Server name"
              className="w-full p-3 rounded-lg bg-zinc-700 text-white placeholder:text-zinc-400 mb-4"
              autoFocus
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowCreateServer(false); setNewServerName(""); }}
                className="px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateServer}
                disabled={!newServerName.trim()}
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Channel Modal */}
      {showCreateChannel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-zinc-800 rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Create a {newChannelType === "voice" ? "Voice" : "Text"} Channel</h2>
            <input
              type="text"
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              placeholder="Channel name"
              className="w-full p-3 rounded-lg bg-zinc-700 text-white placeholder:text-zinc-400 mb-4"
              autoFocus
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowCreateChannel(false); setNewChannelName(""); }}
                className="px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateChannel}
                disabled={!newChannelName.trim()}
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <UserSettingsModal 
        isOpen={showSettings} 
        onClose={() => setShowSettings(false)}
        onUserUpdate={(updatedUser) => setUser(updatedUser)}
      />

      {/* Profile Modal */}
      {showProfile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowProfile(null)}>
          <div className="bg-zinc-800 rounded-xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="h-24 bg-gradient-to-r from-violet-600 to-fuchsia-600 relative">
              <button 
                onClick={() => setShowProfile(null)}
                className="absolute top-2 right-2 p-1.5 bg-black/30 hover:bg-black/50 rounded-full"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 -mt-12">
              <div className="flex items-end justify-between">
                {showProfile.avatar_url ? (
                  <img src={showProfile.avatar_url} alt="" className="w-20 h-20 rounded-full border-4 border-zinc-800 object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 border-4 border-zinc-800 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{showProfile.display_name?.charAt(0) || "U"}</span>
                  </div>
                )}
                <div className="flex gap-2 mb-2">
                  <button 
                    onClick={() => { handleOpenDM(showProfile); setShowProfile(null); }}
                    className="p-2 bg-violet-600 hover:bg-violet-500 rounded-full"
                    title="Send Message"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => { handleAddFriend(showProfile.id); }}
                    className="p-2 bg-green-600 hover:bg-green-500 rounded-full"
                    title="Add Friend"
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-3 p-3 bg-zinc-900 rounded-lg">
                <h3 className="text-xl font-bold">{showProfile.display_name}</h3>
                <p className="text-zinc-400 text-sm">@{showProfile.username}</p>
                {showProfile.custom_status && (
                  <p className="text-sm mt-1 text-zinc-300">{showProfile.custom_status}</p>
                )}
              </div>
              <div className="mt-3 p-3 bg-zinc-900 rounded-lg">
                <p className="text-xs text-zinc-400 uppercase mb-1">About Me</p>
                <p className="text-sm">{showProfile.bio || "No bio yet"}</p>
              </div>
              <div className="mt-3 p-3 bg-zinc-900 rounded-lg">
                <p className="text-xs text-zinc-400 uppercase mb-1">Status</p>
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "w-3 h-3 rounded-full",
                    showProfile.status === 'online' ? 'bg-green-500' :
                    showProfile.status === 'idle' ? 'bg-yellow-500' :
                    showProfile.status === 'dnd' ? 'bg-red-500' : 'bg-zinc-500'
                  )} />
                  <span className="text-sm capitalize">
                    {showProfile.status === 'dnd' ? 'Do Not Disturb' : showProfile.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && activeServer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowInvite(false)}>
          <div className="bg-zinc-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-2">Invite Friends</h2>
            <p className="text-zinc-400 text-sm mb-4">Share this link to invite people to {activeServer.name}</p>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={`https://pravachat.com/invite/${activeServer.invite_code || 'abc123'}`}
                className="flex-1 p-3 rounded-lg bg-zinc-700 text-white"
              />
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(`https://pravachat.com/invite/${activeServer.invite_code || 'abc123'}`);
                  alert('Link copied!');
                }}
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500"
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div 
          className="fixed bg-zinc-900 rounded-lg shadow-xl py-2 min-w-48 z-50"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {contextMenu.type === 'user' && contextMenu.data && (
            <>
              <button onClick={() => { setShowProfile(contextMenu.data); setContextMenu(null); }} className="w-full px-3 py-2 text-left hover:bg-zinc-700 text-sm">
                View Profile
              </button>
              <button onClick={() => { handleOpenDM(contextMenu.data); setContextMenu(null); }} className="w-full px-3 py-2 text-left hover:bg-zinc-700 text-sm">
                Send Message
              </button>
              <button onClick={() => { handleAddFriend(contextMenu.data.id); setContextMenu(null); }} className="w-full px-3 py-2 text-left hover:bg-zinc-700 text-sm">
                Add Friend
              </button>
              <div className="border-t border-zinc-700 my-1" />
              {activeServer?.owner_id === user?.id && contextMenu.data.id !== user?.id && (
                <>
                  <button onClick={() => { handleKickMember(contextMenu.data.id); setContextMenu(null); }} className="w-full px-3 py-2 text-left hover:bg-zinc-700 text-sm text-orange-400">
                    Kick
                  </button>
                  <button onClick={() => { handleBanMember(contextMenu.data.id); setContextMenu(null); }} className="w-full px-3 py-2 text-left hover:bg-zinc-700 text-sm text-red-400">
                    Ban
                  </button>
                  <div className="border-t border-zinc-700 my-1" />
                </>
              )}
              <button onClick={() => { handleBlockUser(contextMenu.data.id); setContextMenu(null); }} className="w-full px-3 py-2 text-left hover:bg-zinc-700 text-sm text-red-400">
                Block
              </button>
            </>
          )}
          {contextMenu.type === 'message' && (
            <>
              <button onClick={() => setContextMenu(null)} className="w-full px-3 py-2 text-left hover:bg-zinc-700 text-sm">
                Reply
              </button>
              <button onClick={() => setContextMenu(null)} className="w-full px-3 py-2 text-left hover:bg-zinc-700 text-sm">
                Copy Text
              </button>
              <div className="border-t border-zinc-700 my-1" />
              <button onClick={() => setContextMenu(null)} className="w-full px-3 py-2 text-left hover:bg-zinc-700 text-sm text-red-400">
                Delete Message
              </button>
            </>
          )}
        </div>
      )}

      {/* DM Modal */}
      {showDM && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDM(null)}>
          <div className="bg-zinc-800 rounded-xl w-full max-w-lg h-[500px] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-700 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
                <span className="text-sm font-bold text-white">{showDM.display_name?.charAt(0) || "U"}</span>
              </div>
              <div>
                <h3 className="font-semibold">{showDM.display_name}</h3>
                <p className="text-xs text-zinc-400">@{showDM.username}</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {dmMessages.length === 0 ? (
                <p className="text-center text-zinc-500 mt-10">No messages yet. Say hi!</p>
              ) : (
                dmMessages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] px-3 py-2 rounded-lg ${msg.sender_id === user?.id ? 'bg-violet-600' : 'bg-zinc-700'}`}>
                      <p className="text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t border-zinc-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={dmInput}
                  onChange={(e) => setDmInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendDM()}
                  placeholder={`Message @${showDM.username}`}
                  className="flex-1 p-3 rounded-lg bg-zinc-700 text-white placeholder:text-zinc-400"
                />
                <button onClick={handleSendDM} className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500">
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close context menu */}
      {contextMenu && <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} />}

      {/* Server Settings Modal */}
      {showServerSettings && activeServer && (
        <ServerSettingsModal 
          server={activeServer}
          onClose={() => setShowServerSettings(false)}
          onServerUpdate={(updatedServer) => {
            setServers(prev => prev.map(s => s.id === updatedServer.id ? { ...s, ...updatedServer } : s));
          }}
        />
      )}

      {/* Search Modal */}
      {showSearch && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-20 z-50" onClick={() => { setShowSearch(false); setSearchResults(null); }}>
          <div className="bg-zinc-800 rounded-xl w-full max-w-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-700">
              <div className="flex items-center gap-3 bg-zinc-900 rounded-lg px-4 py-3">
                <Search className="w-5 h-5 text-zinc-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search messages, users, or servers..."
                  className="flex-1 bg-transparent text-white placeholder:text-zinc-500 outline-none"
                  autoFocus
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")} className="text-zinc-400 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {searchResults && (
              <div className="max-h-96 overflow-y-auto">
                {searchResults.messages?.length > 0 && (
                  <div className="p-4">
                    <h4 className="text-xs font-semibold text-zinc-400 uppercase mb-2">Messages</h4>
                    {searchResults.messages.map((msg: any) => (
                      <div key={msg.id} className="p-3 hover:bg-zinc-700 rounded-lg cursor-pointer">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{msg.display_name}</span>
                          <span className="text-xs text-zinc-500">in #{msg.channel_name}</span>
                        </div>
                        <p className="text-sm text-zinc-300">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                )}
                {searchResults.users?.length > 0 && (
                  <div className="p-4 border-t border-zinc-700">
                    <h4 className="text-xs font-semibold text-zinc-400 uppercase mb-2">Users</h4>
                    {searchResults.users.map((u: any) => (
                      <div key={u.id} className="flex items-center gap-3 p-2 hover:bg-zinc-700 rounded-lg cursor-pointer" onClick={() => { setShowProfile(u); setShowSearch(false); }}>
                        <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center">
                          <span className="text-xs font-bold">{u.display_name?.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-medium text-sm">{u.display_name}</p>
                          <p className="text-xs text-zinc-400">@{u.username}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(!searchResults.messages?.length && !searchResults.users?.length) && (
                  <div className="p-8 text-center text-zinc-400">
                    <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No results found</p>
                  </div>
                )}
              </div>
            )}
            {!searchResults && (
              <div className="p-8 text-center text-zinc-400">
                <p className="text-sm">Start typing to search...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pinned Messages Panel */}
      {showPinnedMessages && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPinnedMessages(false)}>
          <div className="bg-zinc-800 rounded-xl w-full max-w-md max-h-[500px] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
              <h3 className="font-semibold">Pinned Messages</h3>
              <button onClick={() => setShowPinnedMessages(false)} className="p-1 hover:bg-zinc-700 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-96 p-4 space-y-3">
              {pinnedMessages.length === 0 ? (
                <div className="text-center py-8 text-zinc-400">
                  <Pin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No pinned messages</p>
                </div>
              ) : (
                pinnedMessages.map((msg: any) => (
                  <div key={msg.id} className="p-3 bg-zinc-700 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{msg.display_name}</span>
                      <span className="text-xs text-zinc-400">{new Date(msg.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Discover Servers Modal */}
      {showDiscover && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDiscover(false)}>
          <div className="bg-zinc-800 rounded-xl w-full max-w-3xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Discover Servers</h2>
                <button onClick={() => setShowDiscover(false)} className="p-2 hover:bg-zinc-700 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-center gap-3 bg-zinc-900 rounded-lg px-4 py-3">
                <Search className="w-5 h-5 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search for servers..."
                  className="flex-1 bg-transparent text-white placeholder:text-zinc-500 outline-none"
                />
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-96">
              {discoverServers.length === 0 ? (
                <div className="text-center py-12 text-zinc-400">
                  <Compass className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">No servers to discover</p>
                  <p className="text-sm">Create your own server or join with an invite link!</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {discoverServers.map((server: any) => (
                    <div key={server.id} className="bg-zinc-900 rounded-xl p-4 hover:bg-zinc-700/50 transition-colors">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
                          <span className="text-xl font-bold text-white">{server.name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">{server.name}</h3>
                          <p className="text-xs text-zinc-400">{server.member_count || 0} members</p>
                        </div>
                      </div>
                      <p className="text-sm text-zinc-400 mb-3 line-clamp-2">{server.description || "No description"}</p>
                      <button
                        onClick={() => handleJoinServer(server.id)}
                        className="w-full py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white font-medium transition-colors"
                      >
                        Join Server
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Channel Modal */}
      {editingChannel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingChannel(null)}>
          <div className="bg-zinc-800 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">Edit Channel</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Channel Name</label>
                <input
                  type="text"
                  value={editChannelName || editingChannel.name}
                  onChange={(e) => setEditChannelName(e.target.value)}
                  className="w-full p-3 rounded-lg bg-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Topic</label>
                <input
                  type="text"
                  value={editChannelTopic || editingChannel.topic || ""}
                  onChange={(e) => setEditChannelTopic(e.target.value)}
                  placeholder="What's this channel about?"
                  className="w-full p-3 rounded-lg bg-zinc-700 text-white placeholder:text-zinc-500"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-between mt-6">
              <button
                onClick={() => handleDeleteChannel(editingChannel.id)}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white"
              >
                Delete Channel
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setEditingChannel(null)}
                  className="px-4 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditChannel}
                  className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
